import { z } from 'zod';
import { type EquipmentType, FUEL_TYPES, OVEN_EQUIPMENT_TYPES, PURCHASE_TIMEFRAMES } from '../constants';

/**
 * Optional AI assistance (only when the server has AI_API_KEY). Everything here produces suggestions a user
 * reviews: drafts open in the email composer, field suggestions are applied through the normal lead
 * endpoints, summaries can be saved as a note. Nothing is sent or written automatically.
 */

export const AI_DRAFT_PURPOSES = ['introduction', 'follow_up', 'quote_follow_up', 'check_in', 'custom'] as const;
export type AiDraftPurpose = (typeof AI_DRAFT_PURPOSES)[number];
export const AI_DRAFT_PURPOSE_LABELS: Record<AiDraftPurpose, string> = {
  introduction: 'Introduction',
  follow_up: 'Follow-up',
  quote_follow_up: 'Quote follow-up',
  check_in: 'Check-in',
  custom: 'Custom',
};

export const AiDraftEmailBody = z
  .object({
    purpose: z.enum(AI_DRAFT_PURPOSES),
    instructions: z.string().trim().max(1000).optional(),
  })
  .refine((v) => v.purpose !== 'custom' || (v.instructions ?? '').length > 0, {
    message: 'Describe what the email should say',
    path: ['instructions'],
  });
export type AiDraftEmailBody = z.infer<typeof AiDraftEmailBody>;

export type AiSummaryResponse = { summary: string; generatedAt: string; model: string };
export type AiDraftEmailResponse = { subject: string; body: string };

export const AI_SUGGESTION_FIELDS = ['purchaseTimeframe', 'interests', 'currentOven', 'budget', 'estimatedValue'] as const;
export type AiSuggestionField = (typeof AI_SUGGESTION_FIELDS)[number];
export const AI_SUGGESTION_FIELD_LABELS: Record<AiSuggestionField, string> = {
  purchaseTimeframe: 'Purchase timeframe',
  interests: 'Interests',
  currentOven: 'Current oven',
  budget: 'Budget',
  estimatedValue: 'Estimated value',
};

/** Where a suggestion came from: a quote from a note/email/timeline entry of this lead and its date. */
export const AiEvidence = z.object({
  quote: z.string().trim().min(1).max(500),
  date: z.string().trim().max(40).nullable(),
  source: z.enum(['note', 'email', 'activity']),
});
export type AiEvidence = z.infer<typeof AiEvidence>;

const ovenTypes = OVEN_EQUIPMENT_TYPES as unknown as [EquipmentType, ...EquipmentType[]];

export const AiCurrentOven = z.object({
  equipmentType: z.enum(ovenTypes),
  brand: z.string().trim().max(120).nullable().default(null),
  model: z.string().trim().max(120).nullable().default(null),
  fuelType: z.enum(FUEL_TYPES).default('unknown'),
  installYear: z.number().int().min(1900).max(2100).nullable().default(null),
});
export type AiCurrentOven = z.infer<typeof AiCurrentOven>;

const money = z.number().positive().max(10_000_000);

/** One suggested lead field value. `interests` values are product category keys to ADD to the lead. */
export const AiFieldSuggestion = z.discriminatedUnion('field', [
  z.object({ field: z.literal('purchaseTimeframe'), value: z.enum(PURCHASE_TIMEFRAMES).exclude(['unknown']), evidence: AiEvidence }),
  z.object({ field: z.literal('interests'), value: z.array(z.string().max(60)).min(1).max(20), evidence: AiEvidence }),
  z.object({ field: z.literal('currentOven'), value: AiCurrentOven, evidence: AiEvidence }),
  z.object({ field: z.literal('budget'), value: money, evidence: AiEvidence }),
  z.object({ field: z.literal('estimatedValue'), value: money, evidence: AiEvidence }),
]);
export type AiFieldSuggestion = z.infer<typeof AiFieldSuggestion>;

export type AiSuggestFieldsResponse = { suggestions: AiFieldSuggestion[] };
